import { useState, useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const Categories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    is_active: true
  })

  // Fetch categories
  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/categories`)
      if (response.ok) {
        const data = await response.json()
        setCategories(data)
      } else {
        setError('Failed to fetch categories')
      }
    } catch (error) {
      setError('Error fetching categories: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      is_active: true
    })
    setShowAddForm(false)
    setEditingCategory(null)
  }

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      is_active: category.is_active !== false
    })
    setEditingCategory(category)
    setShowAddForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        alert('Please login as admin first')
        return
      }

      const method = editingCategory ? 'PUT' : 'POST'
      const url = editingCategory 
        ? `${API_BASE_URL}/api/categories/${editingCategory.id}`
        : `${API_BASE_URL}/api/categories`
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        await fetchCategories() // Refresh the categories list
        resetForm()
        alert(editingCategory ? 'Category updated successfully!' : 'Category added successfully!')
      } else {
        const errorData = await response.json()
        alert('Error: ' + (errorData.message || 'Failed to save category'))
      }
    } catch (error) {
      alert('Error saving category: ' + error.message)
    }
  }

  const handleDelete = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) {
      return
    }

    try {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        alert('Please login as admin first')
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        await fetchCategories() // Refresh the categories list
        alert('Category deleted successfully!')
      } else {
        const errorData = await response.json()
        alert('Error: ' + (errorData.message || 'Failed to delete category'))
      }
    } catch (error) {
      alert('Error deleting category: ' + error.message)
    }
  }

  // Filter categories based on search
  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  return (
    <div className="fade-in">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <h1 style={{ color: 'var(--text-primary)', margin: 0 }}>
          🏷️ Category Management
        </h1>
        <button 
          className="primary-btn"
          onClick={() => setShowAddForm(true)}
          style={{
            background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
            color: '#000',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ➕ Add Category
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '2rem' }}>
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)'
          }}
        />
      </div>

      {/* Add/Edit Category Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            <input
              type="text"
              name="name"
              placeholder="Category Name"
              value={formData.name}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            />
            <input
              type="url"
              name="image_url"
              placeholder="Category Image URL"
              value={formData.image_url}
              onChange={handleInputChange}
              style={{
                padding: '0.75rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}
            />
          </div>
          <textarea
            name="description"
            placeholder="Category Description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              marginTop: '1rem',
              resize: 'vertical'
            }}
          />
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1.5rem',
            alignItems: 'center'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--text-primary)'
            }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              Active Category
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem' }}>
              <button
                onClick={resetForm}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
                  color: '#000',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingCategory ? 'Update Category' : 'Add Category'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Categories List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          ⏳ Loading categories...
        </div>
      ) : error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: '#ff006e',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 0, 110, 0.3)'
        }}>
          ❌ {error}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredCategories.map(category => (
            <div
              key={category.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '1.5rem',
                transition: 'transform 0.3s ease'
              }}
            >
              {category.image_url && (
                <img
                  src={category.image_url}
                  alt={category.name}
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    marginBottom: '1rem'
                  }}
                />
              )}
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                {category.name}
              </h3>
              {category.description && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {category.description}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ 
                  color: category.is_active !== false ? '#00ff88' : '#ff006e', 
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}>
                  {category.is_active !== false ? '✅ Active' : '❌ Inactive'}
                </span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>
                  ID: {category.id}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleEdit(category)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #00d4ff',
                    background: 'transparent',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #ff006e',
                    background: 'transparent',
                    color: '#ff006e',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredCategories.length === 0 && !loading && !error && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)'
        }}>
          🏷️ No categories found
        </div>
      )}
    </div>
  )
}

export default Categories
